import { onWatcherCleanup } from 'vue'

const POPUP_WIDTH = 700
const POPUP_HEIGHT = 700

const centeredPopupCoordinates = (popupWidth: number, popupHeight: number) => {
  const windowWidth = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width
  const windowHeight = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height

  return {
    left: windowWidth / 2 - popupWidth / 2 + window.screenLeft,
    top: windowHeight / 2 - popupHeight / 2 + window.screenTop,
  }
}

const getWindowFeatures = () => {
  const { left, top } = centeredPopupCoordinates(POPUP_WIDTH, POPUP_HEIGHT)

  return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=no,location=no,menubar=no`
}

export const useOAuthPopup = sharedComposable(() => {
  interface PopupContext {
    popup: ReturnType<typeof window.open>
    callback: (token: string) => void
    errorCallback: () => void
  }

  const context = shallowRef<PopupContext | null>(null)

  const open = (path: string, callback: PopupContext['callback'], errorCallback: PopupContext['errorCallback']) => {
    context.value = {
      popup: window.open(path, 'login', getWindowFeatures()),
      callback,
      errorCallback,
    }
  }

  const watcherCleanup = watch(context, (newValue, oldValue) => {
    if (oldValue !== null && newValue !== null && oldValue.popup !== newValue.popup) {
      oldValue.popup?.close()
    }

    if (!newValue?.popup) {
      return
    }

    let handled = false

    const handleMessage = (event: MessageEvent) => {
      if (event.origin === window.location.origin) {
        if (event.data && typeof event.data === 'object') {
          if (event.data.type === 'OAUTH_POPUP_SUCCESS' && event.data.token) {
            cleanup()
            newValue.popup?.close()
            newValue.callback(event.data.token)
          }
        }
      }
    }

    let closedRetries = 0

    const interval = setInterval(() => {
      if (!newValue.popup) {
        cleanup()
      } else if (newValue.popup.closed && !handled) {
        if (closedRetries < 5) {
          closedRetries++
        } else {
          cleanup()
          newValue.errorCallback()
        }
      }
    }, 100)

    window.addEventListener('message', handleMessage, false)

    const cleanup = () => {
      handled = true
      clearInterval(interval)
      window.removeEventListener('message', handleMessage, false)
    }

    onWatcherCleanup(cleanup)
  })

  onScopeDispose(() => {
    watcherCleanup()
    if (context.value?.popup && !context.value.popup.closed) {
      context.value.popup.close()
    }
  })

  return { open }
})
